
import React from 'react';

interface PlanCardProps {
  plan: any;
  isExpanded: boolean;
  onClick: () => void;
}

const PlanCard: React.FC<PlanCardProps> = ({ plan, isExpanded, onClick }) => {
  const isHighlight = plan.highlight;

  return (
    <div 
      onClick={onClick}
      className={`
        group relative cursor-pointer rounded-2xl border transition-all duration-300 ease-out flex flex-col overflow-hidden
        ${isExpanded 
          ? 'bg-white dark:bg-[#1a1a20] shadow-xl scale-[1.02] z-10 border-transparent' 
          : 'bg-white dark:bg-[#111115] hover:bg-slate-50 dark:hover:bg-white/5 border-slate-200 dark:border-white/5'}
        ${isHighlight && !isExpanded ? 'border-emerald-500/50' : ''}
      `}
    >
      {/* Highlight Ribbon */}
      {isHighlight && (
        <div className="absolute top-3 right-3 flex gap-1">
           <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
        </div>
      )}

      {/* Minimal Header */}
      <div className="p-5 flex items-center gap-4">
        <div className={`
          w-10 h-10 rounded-xl flex items-center justify-center text-sm transition-all
          ${isExpanded || isHighlight ? `bg-${plan.color}-500/10 text-${plan.color}-500` : 'bg-slate-100 dark:bg-white/5 text-slate-400'}
        `}>
          <i className={`fas ${plan.icon}`}></i>
        </div>
        
        <div className="flex-1">
          <h3 className="text-[11px] font-black text-slate-800 dark:text-white uppercase tracking-wider">{plan.name}</h3>
          <div className="flex items-baseline gap-1">
            <span className={`text-sm font-black ${isHighlight ? 'text-emerald-500' : 'text-slate-500 dark:text-slate-400'}`}>{plan.price}</span>
            {plan.id !== 'enterprise' && <span className="text-[9px] font-bold text-slate-300">/ {plan.period}</span>}
          </div>
        </div>

        <div className={`w-6 h-6 rounded-full border border-slate-100 dark:border-white/10 flex items-center justify-center text-[9px] text-slate-300 transition-transform duration-300 ${isExpanded ? 'rotate-180 bg-slate-50 dark:bg-white/10' : ''}`}>
          <i className="fas fa-chevron-down"></i>
        </div>
      </div>

      {/* Expandable Content */}
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-5 pb-5 pt-0">
          <div className="h-px w-full bg-slate-100 dark:bg-white/5 mb-4"></div>
          
          <ul className="space-y-2.5 mb-5">
            {plan.features.map((feature: string, i: number) => (
              <li key={i} className="flex items-start gap-2.5">
                <i className={`fas fa-check mt-1 text-[8px] ${isHighlight ? 'text-emerald-500' : 'text-slate-300'}`}></i>
                <span className="text-[10px] font-bold text-slate-600 dark:text-slate-300 leading-tight">{feature}</span>
              </li>
            ))}
          </ul>

          <button 
            onClick={(e) => {
              e.stopPropagation();
              if (plan.url) window.open(plan.url, '_blank');
            }}
            className={`
              w-full py-3 rounded-xl text-[10px] font-black uppercase tracking-[0.15em] transition-all
              ${plan.id === 'free' 
                ? 'bg-slate-100 dark:bg-white/5 text-slate-400 cursor-default' 
                : isHighlight
                  ? 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-500/20 active:scale-95'
                  : 'bg-slate-800 dark:bg-white text-white dark:text-slate-900 hover:opacity-90 active:scale-95'}
            `}
          >
            {plan.buttonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlanCard;
